import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplet, AlertTriangle, CheckCircle2, Wrench } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const Spezialgutachten = () => {
  const problemAreas = [
    'Kellerabdichtung',
    'Aufsteigende Feuchtigkeit',
    'Kondensationsschäden',
    'Leckagen',
    'Schimmelbildung',
    'Durchfeuchtete Wände',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col">
              <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 text-brand-600 font-medium">
                <Droplet className="w-5 h-5" />
                <span>Spezialgutachten</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-brand-900 leading-tight">
                Feuchtigkeitsschäden – klar analysiert, richtig gelöst
              </h1>
              <p className="text-xl text-brand-600 leading-relaxed">
                Wir liefern eine klare Sanierungsanweisung statt nur Beschreibungen. 
                Zusammenarbeit mit Systempartnern wie KÖSTER Bauchemie für nachhaltige Abdichtung.
              </p>
              </div>
              <div className="mt-10">
              <Link to="/kontakt">
                <Button 
                  size="lg" 
                  className="bg-brand-900 hover:bg-brand-800 text-white px-8 py-3 text-base transition-all duration-300 hover:shadow-lg"
                >
                  Gutachten anfragen
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              </div>
            </div>
            <div className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1768321910101-bdf80fb1ec56"
                alt="Baudetail Sanierung"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Areas */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-brand-900 mb-4">
              Häufige Feuchtigkeitsprobleme
            </h2>
            <p className="text-xl text-brand-600 max-w-3xl mx-auto">
              Von der Ursachenanalyse bis zur Sanierungslösung
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problemAreas.map((area, index) => (
              <div 
                key={index} 
                className="p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex items-center space-x-3"
              >
                <AlertTriangle className="w-6 h-6 text-brand-700 flex-shrink-0" />
                <span className="font-medium text-brand-900">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1771582115253-b77d3c71d500"
                alt="Technische Analyse"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-brand-900 mb-6">
                Von der Diagnose zur Lösung
              </h2>
              <p className="text-lg text-brand-600 leading-relaxed mb-6">
                Feuchtigkeitsschäden sind komplex. Die Ursachen können vielfältig sein, 
                und falsche Sanierungsmaßnahmen verschlimmern oft das Problem.
              </p>
              <p className="text-lg text-brand-600 leading-relaxed mb-8">
                Unsere Spezialgutachten gehen über die reine Schadensbeschreibung hinaus. 
                Sie erhalten:
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-brand-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-brand-900 mb-1">Ursachenanalyse</h3>
                    <p className="text-brand-600">
                      Präzise Identifikation der Feuchtigkeitsquelle und Schadensursache
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-brand-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-brand-900 mb-1">Schadensumfang</h3>
                    <p className="text-brand-600">
                      Bewertung des aktuellen Schadens und möglicher Folgeschäden
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-brand-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-brand-900 mb-1">Sanierungskonzept</h3>
                    <p className="text-brand-600">
                      Detaillierte Anweisungen für nachhaltige Sanierungsmaßnahmen
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-brand-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-brand-900 mb-1">Systemlösungen</h3>
                    <p className="text-brand-600">
                      Empfehlungen geprüfter Abdichtungssysteme (z.B. KÖSTER Bauchemie)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-4xl mx-auto text-center">
          <Wrench className="w-16 h-16 text-brand-700 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-brand-900 mb-6">
            Systempartner für nachhaltige Lösungen
          </h2>
          <p className="text-lg text-brand-600 leading-relaxed mb-6">
            Wir arbeiten mit führenden Herstellern wie KÖSTER Bauchemie zusammen, 
            um Ihnen geprüfte und bewährte Abdichtungssysteme zu empfehlen.
          </p>
          <p className="text-lg text-brand-600 leading-relaxed">
            Das stellt sicher, dass die Sanierung nicht nur das Problem behebt, 
            sondern langfristig Bestand hat.
          </p>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-brand-900 mb-12 text-center">
            Unser Vorgehen
          </h2>
          <div className="space-y-8">
            <div className="flex items-start space-x-6 p-6 bg-brand-50 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-brand-900 text-white rounded-full flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-brand-900 mb-2">Vor-Ort-Begehung</h3>
                <p className="text-brand-600">
                  Detaillierte Inspektion und Dokumentation der Feuchtigkeitsschäden
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-6 p-6 bg-brand-50 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-brand-900 text-white rounded-full flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-brand-900 mb-2">Messungen & Analysen</h3>
                <p className="text-brand-600">
                  Feuchtigkeitsmessungen, Materialproben und technische Untersuchungen
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-6 p-6 bg-brand-50 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-brand-900 text-white rounded-full flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-brand-900 mb-2">Gutachtenerstellung</h3>
                <p className="text-brand-600">
                  Umfassende Dokumentation mit Ursachenanalyse und Sanierungskonzept
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-6 p-6 bg-brand-50 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-brand-900 text-white rounded-full flex items-center justify-center font-bold text-lg">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold text-brand-900 mb-2">Begleitung (optional)</h3>
                <p className="text-brand-600">
                  Unterstützung bei der Umsetzung und Qualitätssicherung der Sanierung
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-8">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900">
            Feuchtigkeitsschäden richtig angehen
          </h2>
          <p className="text-xl text-brand-600 max-w-3xl">
            Lassen Sie uns die Ursachen finden und ein nachhaltiges Sanierungskonzept entwickeln.
          </p>
          <Link to="/kontakt">
            <Button 
              size="lg" 
              className="bg-brand-900 hover:bg-brand-800 text-white px-10 py-3 text-lg transition-all duration-300 hover:shadow-lg"
            >
              Spezialgutachten beauftragen
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Spezialgutachten;
